import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Sparkles, Key, Save } from "lucide-react";
import { toast } from "sonner";

const PROVIDERS = {
  anthropic: {
    label: "Anthropic Claude",
    models: [
      "claude-sonnet-4-5-20250929",
      "claude-sonnet-4-6",
      "claude-opus-4-6",
      "claude-haiku-4-5-20251001",
    ],
  },
  openai: {
    label: "OpenAI",
    models: ["gpt-5.2", "gpt-5.1", "gpt-5-mini", "gpt-4o", "gpt-4.1"],
  },
  gemini: {
    label: "Google Gemini",
    models: ["gemini-3-flash-preview", "gemini-3.1-pro-preview", "gemini-2.5-pro", "gemini-2.5-flash"],
  },
};

export default function AdminSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [provider, setProvider] = useState("anthropic");
  const [model, setModel] = useState("claude-sonnet-4-5-20250929");
  const [useDefault, setUseDefault] = useState(true);
  const [apiKey, setApiKey] = useState("");
  const [hasCustomKey, setHasCustomKey] = useState(false);

  useEffect(() => {
    api.get("/settings/ai")
      .then((r) => {
        setProvider(r.data.provider);
        setModel(r.data.model);
        setUseDefault(r.data.use_default_key);
        setHasCustomKey(r.data.has_custom_key);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {
        provider,
        model,
        use_default_key: useDefault,
        api_key: useDefault ? null : (apiKey || null),
      };
      const { data } = await api.put("/settings/ai", body);
      setHasCustomKey(data.has_custom_key);
      setApiKey("");
      toast.success("AI settings saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-sm text-slate-500">Loading…</div>;
  }

  return (
    <div className="p-8 max-w-3xl" data-testid="admin-settings-page">
      <div className="mb-6">
        <div className="text-xs font-bold uppercase tracking-widest text-blue-700 mb-2">Admin · Settings</div>
        <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900">AI Configuration</h1>
        <p className="text-sm text-slate-500 mt-1">Control which model powers the SQL query optimizer.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-6 space-y-6">
        {/* Default vs custom toggle */}
        <div className="flex items-start justify-between gap-4 pb-5 border-b border-slate-100">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-blue-600" />
              <Label className="font-heading font-bold text-slate-900">Use default Emergent key</Label>
            </div>
            <p className="text-xs text-slate-500">
              Use the platform-managed universal LLM key. Charges go against your Emergent universal balance.
            </p>
          </div>
          <Switch
            checked={useDefault}
            onCheckedChange={setUseDefault}
            data-testid="settings-use-default-toggle"
          />
        </div>

        {!useDefault && (
          <div className="space-y-2 p-4 bg-amber-50 border border-amber-200 rounded-md">
            <div className="flex items-center gap-2">
              <Key className="w-4 h-4 text-amber-700" />
              <Label className="text-xs font-bold uppercase tracking-wider text-amber-800">Custom API Key</Label>
            </div>
            <Input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={hasCustomKey ? "•••••••••••••••••• (leave blank to keep current)" : "Paste your provider API key"}
              className="font-mono text-xs"
              data-testid="settings-custom-key-input"
            />
            <p className="text-[11px] text-amber-700">
              Your key is stored encrypted-at-rest in MongoDB and is never returned in API responses.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Provider</Label>
            <Select value={provider} onValueChange={(v) => {
              setProvider(v);
              setModel(PROVIDERS[v].models[0]);
            }}>
              <SelectTrigger data-testid="settings-provider-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(PROVIDERS).map(([k, p]) => (
                  <SelectItem key={k} value={k}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Model</Label>
            <Select value={model} onValueChange={setModel}>
              <SelectTrigger data-testid="settings-model-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PROVIDERS[provider].models.map((m) => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="text-xs font-bold uppercase tracking-wider text-blue-800 mb-1">Recommended for SQL</div>
          <div className="text-sm text-blue-900">
            <span className="font-mono">claude-sonnet-4-5-20250929</span> handles MS SQL optimization reasoning very well — great default.
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-700 hover:bg-blue-800 text-white"
            data-testid="settings-save-button"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving…" : "Save Settings"}
          </Button>
        </div>
      </div>

      <div className="mt-6 text-xs text-slate-400">
        <span className="font-mono">{hasCustomKey ? "✓ Custom key configured" : useDefault ? "✓ Using default Emergent key" : "⚠ No custom key set"}</span>
      </div>
    </div>
  );
}
